import { useNavigate } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'
import { CheckoutRules } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import { selectLastAppliedRules } from '../../redux/slices/checkout'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import resolveCheckoutRuleRoute from '../../utils/checkoutRuleRoute'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'

const FUNCTION_NAME = 'useCheckoutRuleRecovery'

/** Every value of the rule-type enum, for narrowing an untyped rule name. */
const KNOWN_RULE_TYPES = Object.values(CheckoutRules) as string[]

const isCheckoutRule = (name: string): name is CheckoutRules =>
  KNOWN_RULE_TYPES.includes(name)

/**
 * Turning a checkout-rule rejection into a redirect rather than a dead end.
 *
 * The whole of COM-950's behaviour, and the narrow half of
 * `useCheckoutRuleRecovery` below. Two callers reach it from opposite
 * directions: `useCheckoutRulePreflight` asks the engine on cart entry and
 * routes on what comes back, and `useOrderConfirmation` routes on what a
 * `createOrder` rejection named. Both end at `IConfig.checkoutRuleRoutes`, the
 * storefront's map from a rule type to the screen that satisfies it.
 *
 * **Separate from `useCheckoutRuleRecovery` because it reads no state at
 * render time**, and both callers are mounted somewhere that makes that
 * matter. `useOrderConfirmation` is mounted by `EventManager` on *every*
 * container, so a `useAppSelector` here would subscribe every screen in the
 * app to `lastAppliedRules` for a value neither caller reads.
 *
 * ⚠️ **Inert on the fleet today, on three independent counts.** The rule
 * engine fails open until COM-736 step 2, so `resultCheck` never goes false.
 * `createOrder` does not enforce required rules until COM-1081, so no
 * rejection names one of these rules. And no storefront authors
 * `checkoutRuleRoutes` until COM-951. Tests are the evidence that the wiring
 * works; staging cannot be, and a green suite is not evidence that any of the
 * three landed.
 */
export const useCheckoutRuleRedirect = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const navigate = useNavigate()

  /**
   * Sends the shopper to the screen that fixes the first routable rule named,
   * and answers which one that was.
   *
   * Returns the rule type on a redirect and `null` otherwise, so a caller that
   * needs to remember where it sent them — `useCheckoutRulePreflight` and its
   * loop guard — does not have to repeat the route resolution to find out.
   *
   * **The first routable type wins.** A rejection names every rule that
   * failed, and a shopper stands on one screen. Ordering is the caller's:
   * `getFailedCheckoutRules` preserves the server's, and the pre-flight
   * preserves the engine's.
   *
   * **Deliberately unlatched.** The loop guard lives in the pre-flight, the
   * only caller that can re-enter this on a screen the shopper did not ask
   * for. A rejection that repeats is a shopper who tried again, and they are
   * owed the redirect a second time.
   *
   * **Config is read through `useAppStore`, not `useAppSelector`.** Both
   * callers run this from a callback a network round trip after its closure
   * was built — Apollo's `onCompleted`, and the `catch` around `createOrder` —
   * so a render-time snapshot is as old as the request. COM-974's rule.
   */
  const redirectToUnmetRule = (types: string[]): CheckoutRules | null => {
    const { config, storeName, sessionId } = store.getState().defaultSlice
    const ruleTypes = types.filter(isCheckoutRule)

    /*
     * Nothing named is a checkout rule, so this feature has nothing to say.
     *
     * The guard is **after** the filter, not before it. Returning only on an
     * empty input would let a rejection carrying joi's validation messages
     * fall through to the toast below and tell the shopper their order was
     * blocked by a rule that does not exist.
     *
     * Defensive rather than live, per the COM-736 convention:
     * `getFailedCheckoutRules` already filters against this enum, and the
     * pre-flight passes rule types straight off the response. Neither caller
     * can reach it today.
     */
    if (!ruleTypes.length) return null

    const resolved = ruleTypes.reduce<
      { ruleType: CheckoutRules; path: string } | undefined
    >((found, ruleType) => {
      if (found) return found
      const path = resolveCheckoutRuleRoute(
        config?.checkoutRuleRoutes?.[ruleType],
        config?.routes,
        storeName,
        sessionId,
      )
      return path ? { ruleType, path } : undefined
    }, undefined)

    if (resolved) {
      /*
       * `replace`, never `usePushView`, and this is a security requirement
       * rather than a Back-button preference.
       *
       * Both halves of the path come from the URL, and a slug carrying `%2f`
       * decodes to `//evil.com`. `history.replace` calls `replaceState` bare,
       * so the browser's same-origin check throws and nothing navigates;
       * `history.push` swallows that and falls back to
       * `window.location.assign`, which navigates cross-origin. Verified
       * against the installed `@remix-run/router@1.21.0`, and spelled out at
       * length in `templates/OrderConfirmation.tsx`.
       *
       * It reads correctly for the shopper too. The URL they are leaving is a
       * cart that will not advance, or a confirmation screen for an order
       * that does not exist. Neither is worth a Back button.
       */
      navigate(resolved.path, { replace: true })
      return resolved.ruleType
    }

    /*
     * Nowhere to send them, so say so and leave them where they are.
     *
     * Never a throw and never an error screen: the shopper's cart is intact
     * and this storefront simply has no screen for the rule that blocked
     * them. The toast points at the conversation, which is the only place the
     * order can still be finished.
     */
    const message = getAlertErrorMessage(
      AlertMessageType.CheckoutRuleRouteMissing,
      config?.texts,
    )
    if (message) dispatch(addAlert({ message }))

    /*
     * `authoredRoutes` separates the two ways this arm is reached, which the
     * COM-736 convention says to label rather than leave a reader to assume.
     *
     * An empty object is the **live** case: no storefront authors
     * `checkoutRuleRoutes` at all until COM-951, so every rejection lands
     * here. A populated one means the storefront authored an entry and it did
     * not resolve — a token it does not recognise, or a path its own `routes`
     * table does not register. That second case is an authoring bug and the
     * only one worth alerting on.
     */
    const authoredRoutes = ruleTypes.reduce<Record<string, string>>(
      (entries, ruleType) => {
        const authored = config?.checkoutRuleRoutes?.[ruleType]
        return authored ? { ...entries, [ruleType]: authored } : entries
      },
      {},
    )

    const context = generateDataDogContext({
      title: 'checkout rule blocked the shopper with no screen to fix it',
      extraInfo: {
        function: FUNCTION_NAME,
        ruleTypes: JSON.stringify(types),
        knownRuleTypes: JSON.stringify(ruleTypes),
        authoredRoutes: JSON.stringify(authoredRoutes),
        slug: JSON.stringify(storeName ?? null),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        `checkout rules ${JSON.stringify(
          types,
        )} blocked the order and none resolved to a route; authored entries ${JSON.stringify(
          authoredRoutes,
        )}, so the shopper stays put and cannot satisfy the rule here`,
      ),
      context,
    )

    return null
  }

  return redirectToUnmetRule
}

/**
 * The redirect, plus the question a cart screen asks before offering to place
 * an order.
 *
 * The shape COM-950 specifies. Nothing in COM-950 reads
 * `hasUnmetBlockingRules` — both of its callers use `useCheckoutRuleRedirect`
 * above, because neither renders from it. The reader is COM-1045's *Change*
 * rows and COM-1046's *Continuar* CTA, and the predicate lives here rather
 * than in those tickets because it belongs beside the field it interprets.
 */
export const useCheckoutRuleRecovery = () => {
  const redirectToUnmetRule = useCheckoutRuleRedirect()
  const lastAppliedRules = useAppSelector(selectLastAppliedRules)

  /**
   * Whether the engine's last answer left a blocking rule unsatisfied.
   *
   * `blocks && !checked`, not `resultCheck`: the top-level flag answers for
   * the whole session, and a shopper can only be sent to one screen, so the
   * per-rule pair is the one that says *which*.
   *
   * `undefined` — no pre-flight has run — answers `false`. A cart that has
   * not asked must not claim to know that something blocks it.
   */
  const hasUnmetBlockingRules = (lastAppliedRules ?? []).some(
    (rule) => rule.blocks && !rule.checked,
  )

  return { redirectToUnmetRule, hasUnmetBlockingRules }
}

export default useCheckoutRuleRecovery
